import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  getSsoConfig,
  getSsoSession,
  redirectToSsoLogin,
  storeSsoSession,
  validateSsoToken,
} from 'auth/ssoClient'
import { PageLoader } from 'components/common/Loader'
import { decryptQueryParams, encryptQueryParams } from 'utils/crypto'

const SSO_LINK_PARAMS = ['token', 'ssotoken', 'sso_token', 'kit_token', 'deviceid', 'returnurl']

/**
 * Route guard: the entity screens need an identity to call the API as.
 * Accepted, in order:
 *   1. an SSO token in the link (`?token=…`, the way the main Kit19 app opens
 *      View / Edit entity) -> validated through Authorize/ValidateToken and the
 *      returned user becomes the session. This happens in place, behind a loader:
 *      the user never sees a sign-in page;
 *   2. the (encrypted) link carries UserId/ParentId itself;
 *   3. an SSO session already stored in localStorage;
 *   4. SSO disabled -> development identity.
 * Only a visit with none of these goes to /login, which forwards to the Kit19 SSO page.
 */
export function RequireSession({ children }: { children: ReactElement }) {
  const location = useLocation()
  const navigate = useNavigate()
  const sso = getSsoConfig()

  const params = decryptQueryParams(location.search)
  const lowered = new Map(Object.entries(params).map(([k, v]) => [k.toLowerCase(), v]))
  const linkToken =
    lowered.get('token') || lowered.get('ssotoken') || lowered.get('sso_token') || lowered.get('kit_token')
  const deviceId = lowered.get('deviceid') || null

  const session = getSsoSession()
  const tokenIsCurrentSession = !!linkToken && linkToken === session.token && session.isAuthenticated

  // The first paint for a link that carries a token must be the loader. Starting this
  // as false let the /login redirect below win the race on a first visit, so the
  // token in the link was never used.
  const [validating, setValidating] = useState<boolean>(() => sso.enabled && !!linkToken && !tokenIsCurrentSession)

  // What the address bar shows once the session is in hand: the same path with
  // whatever else the link carried re-sealed into a fresh q=. The SSO token and
  // device id are dropped; nothing is left readable, not even a legacy entityID.
  const cleanPath = useMemo(() => {
    const rest: Record<string, string> = {}
    for (const [key, value] of Object.entries(decryptQueryParams(location.search))) {
      if (!SSO_LINK_PARAMS.includes(key.toLowerCase()) && value !== '') rest[key] = value
    }
    return location.pathname + (Object.keys(rest).length ? `?${encryptQueryParams(rest)}` : '')
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!sso.enabled || !linkToken) return
    if (tokenIsCurrentSession) {
      // already signed in as this user: just keep the JWT out of the address bar
      navigate(cleanPath, { replace: true })
      return
    }

    let cancelled = false
    setValidating(true)

    void (async () => {
      const result = await validateSsoToken(linkToken)
      if (cancelled) return

      if (result.valid && result.userData) {
        storeSsoSession(linkToken, result.userData, deviceId)
        setValidating(false)
        navigate(cleanPath, { replace: true })
      } else {
        // An expired or rejected token is not something to read an error about:
        // straight to SSO, and back to this same record once signed in. The
        // loader stays up while the browser leaves.
        redirectToSsoLogin(cleanPath)
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken, deviceId, tokenIsCurrentSession, sso.enabled])

  if (!sso.enabled) return children

  if (validating) return <PageLoader />

  // Only an SSO session opens a record. A link carrying UserId / ParentId /
  // ApiToken but no token used to render on that identity alone, and the API
  // then rejected the token — the user saw an error where a sign-in belonged.
  if (getSsoSession().isAuthenticated) return children

  return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
}
